// 受注処理（OrderNewComponents）の入力から、契約詳細入力画面（AkbsContractForm）の新規登録の初期値を作る
//
// 対象（区分は関係なし。当てはまれば対象にして、実際に登録するかはユーザーが決める）
//   ・日常清掃（categoryCode 3：order.orderEverydays）、管理員業務（categoryCode 4：order.orderManagerWorks）
//   ・ラウンドプラス（categoryCode 2：order.orderRlsPlus）のうち、固定清掃があるもの（ラウンドクリーンプラス・ラウンドダブルプラス）
//   ・作業内容（details）のうち「調整費」は除く（受注画面でも見出しを出していない）
// 契約コードの単位：作業曜日・時間の行（【1】【2】…）を曜日が重ならないものどうしでまとめたもの1つ＝契約コード1つ（groupRemarks）
//   人数（見積で決めた people）がまとまりの数より多ければ、足りない分は勤務が空の契約コードを足す（blank。開始日・作業内容・時間指定・祝日対応は項目の値）
//   （2026-09-22 は最後のまとまりの写しにしていたが、勝手に写すのをやめて空にし、画面で「○名の手配」と強調して入力してもらう。ユーザー決定 2026-09-23）
//   契約詳細の行数で清掃員のシフトの必要人数が決まり、受注金額から時給単価も出しているので、契約コードは人数以上でないといけない
//   （同じ人が同じ曜日に2回入る受注は人数より多くなるので、多い分は OK。少なければ申請を止める）
// 年末年始は契約詳細に持つ所が無いので写さない
//
// 一時保存のあとに受注が変わったときは、画面が開くたびに、ここで作った初期値を行の「元の値」に入れ直し、画面の値と違う欄を黄色にする（AkbsContractForm.syncOrder）
// どの契約コードが受注のどの1件から作られたかは、契約コードに控えた orderKey（この中の entry.orderKey）と itemKey で見分ける
import BigNumber from 'bignumber.js'
import { WEEK_LIST, ATTENDANCE } from '@libs/constantsAKBS'
import * as utils from '@libs/utils'
import { isDaysRemark, daysOf, hoursFromTimes } from '@libs/orderRemark'

// 固定清掃があるラウンドプラスの種別
const FIXED_RLS_PLUS = ['ラウンドクリーンプラス', 'ラウンドダブルプラス']

// 受注の作業内容コード → 契約詳細の作業内容（WORK_SELECTIONS の id）
// 名前は似ていてもコードの持ち方が全然違うので、対応はここで決め打ちする（ユーザー指定）。ここに無いコードは空（画面で選んでもらう）
const WORK_CODE_MAP = {
  '0001100': '30', // 管理員（日常清掃含）
  '0001110': '20', // 管理員
  '0001200': '10', // 日常清掃
  '0001210': '40'  // 巡回員
}

// 受注の曜日（'月'…）→ 契約詳細の曜日の id（'Monday'…）
const WEEK_ID = {}
for (const w of WEEK_LIST) {
  WEEK_ID[w.name] = w.id
}

/**
 * 受注の選択値（OrderItemSelect は ['1'] のような配列で持つ）の先頭
 */
function first(value) {
  const v = Array.isArray(value) ? value[0] : value
  return v === null || v === undefined ? '' : String(v)
}

/**
 * 時間を契約詳細入力画面の形（AkbsTimeInput と同じ「8:00」）に揃える
 */
function toTime(value) {
  const m = String(value || '').match(/^(\d{1,2}):(\d{1,2})$/)
  return m ? `${Number(m[1])}:${m[2].padStart(2, '0')}` : ''
}

// ラウンドプラスの固定清掃の祝日勤務の初期値（100＝FULL。受注に祝日対応の欄が無いので画面で変えられる）
const RLS_PLUS_HOLIDAY_DEFAULT = 100

/**
 * 祝日対応（1：FULL、2：ゴミ出しのみ、0：無）→ 契約詳細の祝日勤務
 * FULL・無はそのまま。ゴミ出しのみは「時間指定」を選んだ状態にして、時間はユーザーに入れてもらう（空）
 * 選んでいなければ null（画面ではどれも選んでいない状態）
 */
function toHoliday(value) {
  const v = first(value)
  if (v === '1') return 100
  if (v === '0') return 0
  if (v === '2') return ''
  return null
}

/**
 * 作業曜日・時間の1件 → 契約の行の曜日の欄
 * 勤務形態は受注も同じ名前（毎週・第1…・隔週）で持っているので、名前で ATTENDANCE の選択肢に直す
 * 曜日ごとの形（isDaysRemark。契約詳細入力画面と同じ持ち方）はそのまま写す。以前の形は行の時間1組を選んだ曜日ぜんぶに入れる
 */
function toDays(remark) {
  const days = {}
  if (isDaysRemark(remark)) {
    for (const d of daysOf(remark)) {
      const start = toTime(d.startTime)
      const end = toTime(d.endTime)
      // 実働が空なら、受注画面と同じ決め方（時間帯の幅。6時間を超えれば休憩1時間を引く）で出す
      const hours = Number(d.hours) ? String(Number(d.hours)) : hoursFromTimes(start, end)
      days[WEEK_ID[d.week]] = {
        attendance: ATTENDANCE.filter(a => d.weekDetail.includes(a.name)),
        start,
        end,
        workingHours: hours
      }
    }
    return days
  }
  const workDays = remark.workDays || []
  const weekDetail = remark.weekDetail && !Array.isArray(remark.weekDetail) ? remark.weekDetail : {}
  const start = toTime(remark.startTime)
  const end = toTime(remark.endTime)
  let hours = remark.hours === null || remark.hours === undefined || remark.hours === '' || !Number(remark.hours) ? '' : String(Number(remark.hours))
  // 受注で実働時間が 0・空なら、時間帯（開始〜終了）から出す（休憩は引かないので、画面で直せる。ユーザー指示）
  if (!hours && start && end) {
    const fromTime = utils.getHours(start, end)
    if (fromTime > 0) {
      hours = String(Math.round(fromTime * 100) / 100)
    }
  }
  for (const name of Object.keys(WEEK_ID)) {
    if (!workDays.includes(name)) {
      continue
    }
    const names = weekDetail[name] || []
    days[WEEK_ID[name]] = {
      attendance: ATTENDANCE.filter(a => names.includes(a.name)),
      start,
      end,
      workingHours: hours
    }
  }
  return days
}

/**
 * 作業内容の名前（見積の内容には改行が入っていることがあるので、ダイアログや見出しに出すために改行を消す）
 */
function toContent(value) {
  return String(value || '').replace(/\r?\n/g, '').trim()
}

/**
 * 見積で決めた人数（空・数字でなければ 0。'1 人' のような文字は先頭の数字）
 */
function toPeople(value) {
  const n = parseFloat(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * 週の回数（受注は「週 4.5 回」の文字で持つ）→ 数（無ければ 0）
 * 「4.5」の .5 は見積の単価計算の数で、勤務の形（隔週・第n週）としては決まっていない。
 * 形には直さず、月の時間（× 4.3）に直して画面の契約コードの月の時間と比べる（orderMonthlyHours）
 */
function toWeekTimes(value) {
  const m = String(first(value)).match(/(\d+(?:\.\d+)?)/)
  const n = m ? Number(m[1]) : 0
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * 1回の時間（受注の hours。空・数字でなければ 0）
 */
function toHours(value) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * 受注（見積）の月の時間＝ 週の回数 × 1回の時間 × 人数 × 4.3（見積の「○○時間/月」と同じ式・同じ丸め）
 * どれかが無ければ 0（比べない）
 */
export function orderMonthlyHours(weekTimes, hours, people) {
  if (!weekTimes || !hours || !people) {
    return 0
  }
  return Math.floor(BigNumber(weekTimes).times(hours).times(people).times(4.3).times(100)) / 100
}

/**
 * 受注の対象の項目（契約詳細の登録の対象になる作業内容ごと）
 * 画面の概要に「受注の内容」として出し、契約コードの初期値と人数の元にする
 * @param {Object} order 受注のメインタスク
 * @returns [{ key: 受注の中の場所（orderEverydays:0:1 のような、項目の並び番号）, no: 画面で「受注1」と呼ぶ番号, category: 大項目の名前（日常清掃など）, content: 作業内容（【】の中）,
 *             kinds: ラウンドプラスの種別（それ以外は空）, people: 見積で決めた人数, weekTimes: 週の回数, hours: 1回の時間,
 *             monthlyHours: 受注の月の時間（回数 × 時間 × 人数 × 4.3。どれかが無ければ 0）,
 *             workCode, cdContentsWork: 契約詳細の作業内容（WORK_CODE_MAP。無ければ空）, src: 開始日・時間指定・祝日対応を持つもの, remarks: 作業曜日・時間 }]
 */
export function orderItems(order) {
  const list = []
  if (!order) {
    return list
  }
  // 並びは受注画面のカテゴリーの順（ラウンドプラス → 日常清掃 → 管理員業務）にそろえる。「受注1」の番号と契約コードの順もこの順になる
  // ラウンドプラス：固定清掃があるものだけ。時間指定・祝日対応・人数と、作業曜日・時間は o の直下に持つ
  const rls = order.orderRlsPlus || []
  for (let oi = 0; oi < rls.length; oi++) {
    const o = rls[oi]
    const kinds = (o.estimationRoundPlusCategorySelect || []).filter(k => FIXED_RLS_PLUS.includes(k))
    if (!kinds.length) {
      continue
    }
    // 固定清掃の人数は見積で 1 人固定。古い受注は見積の '1 人' の文字が Number で落ちて people が無いので、無ければ 1 にする
    const people = toPeople(o.people) || 1
    // 固定清掃の週の回数は normalCount（count2 は巡回の回数なので使わない）、1回の時間は hours
    const weekTimes = toWeekTimes(o.normalCount)
    const hours = toHours(o.hours)
    list.push({
      key: `orderRlsPlus:${oi}`,
      // 画面で「受注1」「受注2」と呼ぶ番号（並び順）
      no: list.length + 1,
      category: toContent(o.name),
      content: '固定清掃',
      kinds,
      people,
      weekTimes,
      hours,
      monthlyHours: orderMonthlyHours(weekTimes, hours, people),
      workCode: (o.workCode && o.workCode.code) || '',
      cdContentsWork: WORK_CODE_MAP[(o.workCode && o.workCode.code) || ''] || '',
      src: o,
      remarks: o.remarks || []
    })
  }
  // 日常清掃・管理員業務：作業内容（details）ごとに、開始日・時間指定・祝日対応・人数と、作業曜日・時間を持つ
  for (const prop of ['orderEverydays', 'orderManagerWorks']) {
    const items = order[prop] || []
    for (let oi = 0; oi < items.length; oi++) {
      const o = items[oi]
      const details = o.details || []
      for (let di = 0; di < details.length; di++) {
        const det = details[di]
        // 調整費は契約詳細の対象ではない（受注画面でも見出しを出していない）
        if (String(det.content || '').includes('調整費')) {
          continue
        }
        const people = toPeople(det.people)
        // 週の回数は「週 4.5 回」（count2）、1回の時間は hours
        const weekTimes = toWeekTimes(det.count2)
        const hours = toHours(det.hours)
        list.push({
          key: `${prop}:${oi}:${di}`,
          no: list.length + 1,
          category: toContent(o.name),
          content: toContent(det.content),
          kinds: [],
          people,
          weekTimes,
          hours,
          monthlyHours: orderMonthlyHours(weekTimes, hours, people),
          workCode: (o.workCode && o.workCode.code) || '',
          cdContentsWork: WORK_CODE_MAP[(o.workCode && o.workCode.code) || ''] || '',
          src: det,
          remarks: det.remarks || []
        })
      }
    }
  }
  return list
}

/**
 * 項目の表示名（画面の概要と契約の見出しに出す）
 */
export function orderItemName(item) {
  const kinds = item.kinds && item.kinds.length ? `（${item.kinds.join('・')}）` : ''
  return `${item.category ? item.category + ' ' : ''}【${item.content}】${kinds}`
}

/**
 * 受注の人数（対象の項目の、見積で決めた人数の合計）
 * 契約コードはこの数以上ないといけない（申請時に確かめる）
 * @param {Object} order 受注のメインタスク
 */
export function orderPeople(order) {
  return orderItems(order).reduce((sum, item) => sum + item.people, 0)
}

/**
 * 作業曜日・時間の行を、曜日が重ならないものどうしでまとめる（1つのまとまり＝契約コード1つ）
 * 受注の行は1行に開始〜終了が1つしか持てないが、契約詳細の行は曜日ごとに時間を持てるので、
 * 「①月水金 8〜12 ②火木 9〜13」のような1人の勤務は1つにまとめる（ユーザー決定 2026-09-22。人数に関係なくまとめる）
 * ・前から順に、曜日が重ならない最初のまとまりへ入れる。重なれば新しいまとまり（①月 8〜 ②月 13〜 は2つのまま）
 * ・行はまるごと動かす。曜日をばらして別のまとまりへ移すことはしない（①月火水 ②月火金 の金を①へ持っていかない）
 * ・曜日が入っていない行はまとめない（1行で1つ）
 * ・曜日ごとの形の行（isDaysRemark）は1行＝1人なのでまとめない（曜日ごとに時間を持てるので、まとめる必要が無い）
 * @param {Array} remarks 作業曜日・時間
 * @returns [{ indexes: 元の行の並び番号（0 から）, days: まとめた曜日の欄 }]
 */
function groupRemarks(remarks) {
  const groups = []
  for (let ri = 0; ri < remarks.length; ri++) {
    const remark = remarks[ri] || {}
    const days = toDays(remark)
    const dayIds = Object.keys(days)
    const group = dayIds.length && !isDaysRemark(remark) ? groups.find(g => !g.daysShape && !Object.keys(g.days).some(d => dayIds.includes(d))) : null
    if (group) {
      group.indexes.push(ri)
      Object.assign(group.days, days)
    } else {
      groups.push({ indexes: [ri], days, daysShape: isDaysRemark(remark) })
    }
  }
  return groups
}

/**
 * 受注の入力から、契約コードごとの初期値を作る
 * 項目ごとに、作業曜日・時間の行を曜日が重ならないものどうしでまとめ（groupRemarks）、まとまり1つにつき契約コード1つ
 * 人数がまとまりの数より多ければ、足りない分は勤務が空の契約コード（blank）。画面で「○名の手配」と強調して勤務を入れてもらう
 * @param {Object} order 受注のメインタスク
 * @returns [{ orderKey: 受注のどの行から作ったか（項目の key＋まとめた行の並び番号＋空の番号）, name: 表示名（項目名＋何人目＋まとめた行）,
 *             merged: まとめた受注の行の番号（【1】【2】…の数字。2つ以上ならまとめている）, blank: 受注に勤務の行が無い分（勤務は空）, nth: 何人目か（1 から）,
 *             cdContentsWork, startDate, specificTime, kubunHolidayContract, hasCarryingoutGarbageContract（祝日対応が「ゴミ出しのみ」なら 1。それ以外は null）,
 *             fixed: { specificTime, holiday, garbage }（受注で決まっていて画面で変えさせない欄）,
 *             holidayDefault: 祝日勤務が「FULL」の初期値で画面で変えられる（ラウンドプラスの固定清掃。変えたら黄色＋申請・承認時に確認）,
 *             holidayGarbage: 受注の祝日対応が「ゴミ出しのみ」（画面の祝日勤務の欄に出す）,
 *             days: { Monday: { attendance, start, end, workingHours } } }]
 */
export function contractsFromOrder(order) {
  const list = []
  for (const item of orderItems(order)) {
    const groups = groupRemarks(item.remarks)
    const count = Math.max(groups.length, item.people)
    // ラウンドプラスの固定清掃は見積に祝日対応の欄が無い（祝日のゴミ出しは巡回清掃の方で決めている）ので、祝日勤務は「FULL」を初期値にして画面で変えられる
    // 日常清掃・管理員業務は受注で選んであれば変えさせない
    const isRlsPlus = item.kinds.length > 0
    const holiday = toHoliday(item.src.holiday)
    for (let n = 0; n < count; n++) {
      // まとまりが足りない分は勤務が空（空の番号は 0 から）
      const blank = n >= groups.length
      const group = blank ? { indexes: [], days: {} } : groups[n]
      const merged = group.indexes.map(i => i + 1)
      const mergedText = merged.length > 1 ? `（受注の${merged.map(m => `【${m}】`).join('')}をまとめた）` : ''
      list.push({
        orderKey: `${item.key}:${group.indexes.join('-')}:${blank ? n - groups.length : 0}`,
        // どの項目（作業内容）の分か（受注の行の足し引きで orderKey が変わったとき、同じ項目の中で並び順に対応させる）
        itemKey: item.key,
        name: `受注${item.no} ${orderItemName(item)} ${n + 1}人目${mergedText}`,
        merged,
        blank,
        // 何人目か（1 から。空の契約コードの帯に「○人目の手配が必要と思われます」と出す）
        nth: n + 1,
        cdContentsWork: item.cdContentsWork,
        startDate: item.src.startDate || '',
        // 時間指定（1：有、0：無）。選んでいなければ空
        specificTime: first(item.src.specificTime) === '' ? '' : Number(first(item.src.specificTime)),
        kubunHolidayContract: isRlsPlus && holiday === null ? RLS_PLUS_HOLIDAY_DEFAULT : holiday,
        // 見積から受注タスクに写されて受注画面でも変えられない欄は、契約詳細入力画面でも変えさせない（受注で選んであるものだけ）
        // 祝日対応の「ゴミ出しのみ」は区分（時間指定）だけ固定で、時間は画面で入れる
        fixed: {
          specificTime: first(item.src.specificTime) !== '',
          holiday: !isRlsPlus && first(item.src.holiday) !== '',
          // 祝日対応が「ゴミ出しのみ」ならゴミ搬出は「有り」で固定（ユーザー指示 2026-10-09）
          garbage: first(item.src.holiday) === '2'
        },
        holidayDefault: isRlsPlus,
        // 受注の祝日対応が「ゴミ出しのみ」（AKBS には無い区分。時間入力で時間を入れてもらう。画面の祝日勤務のセルの上に「※ゴミ出しのみ」と出す）
        holidayGarbage: first(item.src.holiday) === '2',
        // ゴミ搬出（祝日対応が「ゴミ出しのみ」なら 1：有り。それ以外は受注に欄が無いので入れない）
        hasCarryingoutGarbageContract: first(item.src.holiday) === '2' ? 1 : null,
        days: group.days
      })
    }
  }
  return list
}

/**
 * 開始日が入っていない対象の項目の表示名（契約詳細入力画面へ進む前の確認に使う）
 * 開始日は契約開始日の初期値になり、契約詳細は開始日が無いと申請できないので、受注画面で入れてから進んでもらう
 * @param {Object} order 受注のメインタスク
 * @returns {Array<String>} 無ければ空
 */
export function missingStartDates(order) {
  return orderItems(order).filter(item => !item.src.startDate).map(item => orderItemName(item))
}

/**
 * 契約詳細情報の登録の対象になる受注か（日常清掃・管理員業務がある、または固定清掃のあるラウンドプラスがある）
 * 作業曜日・時間がまだ入っていなくても対象にする
 * @param {Object} order 受注のメインタスク
 */
export function isContractTarget(order) {
  if (!order) {
    return false
  }
  if ((order.orderEverydays || []).length || (order.orderManagerWorks || []).length) {
    return true
  }
  return (order.orderRlsPlus || []).some(o => (o.estimationRoundPlusCategorySelect || []).some(k => FIXED_RLS_PLUS.includes(k)))
}

/**
 * 契約詳細情報の登録の対象になっているカテゴリーの名前（受注画面の案内文に使う）
 * @param {Object} order 受注のメインタスク
 * @returns {Array<String>} 例：['ラウンドプラス', '日常清掃']。対象が無ければ空
 */
export function contractTargetNames(order) {
  const names = []
  if (!order) {
    return names
  }
  if ((order.orderRlsPlus || []).some(o => (o.estimationRoundPlusCategorySelect || []).some(k => FIXED_RLS_PLUS.includes(k)))) {
    names.push('ラウンドプラス')
  }
  if ((order.orderEverydays || []).length) {
    names.push('日常清掃')
  }
  if ((order.orderManagerWorks || []).length) {
    names.push('管理員業務')
  }
  return names
}
